import { useState, useEffect, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom';
import { PlayerData, PlayerStats, ItemSpawnData, EffectSpawnData, CreativeLogData, NoteWarning, Ban, EconomyLogData } from '../types/player'
import { CompensationLogHit } from '../types/compensation'
import { gameRedisService } from '../services/gameRedis'
import { usersApiService } from '../services/usersApi'
import { notesApiService } from '../services/notesApi';
import { cn } from '../utils/cn'
import { 
  User, 
  Shield, 
  Home, 
  Crown, 
  DollarSign, 
  MessageSquare, 
  Network,
  Globe,
  Calendar,
  MapPin,
  Tag,
  Activity,
  RefreshCw,
  AlertCircle,
  Code,
  Trash2,
  Clock,
  Globe2,
  List,
  Box,
  Sparkles,
  Swords,
  LogIn,
  LogOut,
  Users as UsersIcon,
  FileText,
  TriangleAlert,
  Lock,
  Axe,
  Copy,
  Check,
  Link2,
  Search,
  LoaderCircle,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  ArrowRight,
  HandCoins,
  ScrollText
} from 'lucide-react'
import { PlayerAppealsSection } from '../components/appeals/PlayerAppealsSection';
import { useChatTabs } from '../contexts/ChatTabsContext';
import { useNavigate } from 'react-router-dom';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useSwrLoader } from '../hooks/useSwrLoader';
import { read } from '../services/swrCache';
import { DataFreshness } from '../components/DataFreshness';
import { formatDistanceToNow } from 'date-fns';
import { PlayerProfile } from '../components/PlayerProfile';
import { CreateNoteWarningModal } from '../components/CreateNoteWarningModal';
import { CreateBanModal } from '../components/CreateBanModal';
import { BanCard } from '../components/BanCard';
import { Identity } from '../types/auth';
import { EvidenceSection } from '../components/EvidenceSection';
import { TicketLinks } from '../components/TicketLinks';
import { useIsMobile } from '../hooks/useIsMobile';
import { CreatorProfile } from '../components/CreatorProfile';
import { AddEvidenceModal } from '../components/AddEvidenceModal';
import { LinkIncidentModal } from '../components/LinkIncidentModal';
import { elasticsearchService } from '../services/elasticsearchService';
import { useDebounce } from '../hooks/useDebounce';
import { usernameCacheService } from '../services/usernameCache';
import { useCallback } from 'react';
import { RedisKeyInfo } from '../components/RedisKeyInfo';
import { ItemIcon } from '../components/ItemIcon';
import { KanbanLinkedCardsSection } from '../components/kanban/KanbanLinkedCardsSection';
import { ShowAutoModToggle } from '../components/ShowAutoModToggle';
import { countAutoModEntries, filterAutoModEntries } from '../utils/automod';
import { StaffNotesPanel } from '../components/staff/notes/StaffNotesPanel';
import { getStaffPerms } from '../utils/staffPermissions';
import { canViewRelationships } from '../utils/relationshipPermissions';
import { canViewLogbook } from '../utils/logbookPermissions';
import { RelationshipExplorer } from '../components/relationships/RelationshipExplorer';
import { LogbookPlayerTimeline } from '../components/logbook/LogbookPlayerTimeline';
import { useStaffTeams } from '../hooks/useStaffTeams';
import { PageHeader } from '../components/ui/PageHeader';
import { SectionHeader } from '../components/ui/SectionHeader';
import { StatTile } from '../components/ui/StatTile';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonLoader } from '../components/SkeletonLoader';

/** Label / value row for dense info lists inside cards (pair with `divide-y divide-glass-200/10`). */
const InfoRow = ({ label, children }: { label: React.ReactNode; children: React.ReactNode }) => (
  <div className="flex items-center justify-between gap-3 py-2 text-sm">
    <span className="text-glass-400 shrink-0">{label}</span>
    <span className="text-white font-medium text-right min-w-0 break-words tabular-nums">{children}</span>
  </div>
);

const NoteWarningCard = ({ entry, onDelete, canDelete, onLink, onLinkIncident }: { entry: NoteWarning, onDelete: (id: string) => void, canDelete: boolean, onLink: () => void, onLinkIncident: () => void }) => {
  const { id, type, content, creator_id, created_at, uuid, status } = entry;
  const Icon = type === 'note' ? FileText : TriangleAlert;
  const iconTone = type === 'note' ? 'icon-tile-info' : 'icon-tile-warning';

  return (
    <div className="glass-card p-4 space-y-3">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className={cn('icon-tile', iconTone)}>
            <Icon className="w-4 h-4" />
          </span>
          <div className="min-w-0">
            <PlayerProfile uuid={uuid} />
            <p className="text-xs text-glass-400">
              Created by <CreatorProfile creatorId={creator_id} inline />{' '}
              <span className="tabular-nums">{formatDistanceToNow(new Date(created_at), { addSuffix: true })}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <span className={cn('badge badge-dot mr-1', status === 'active' ? 'badge-success' : 'badge-danger')}>
            {status}
          </span>
          <button onClick={onLinkIncident} className="btn-icon btn-icon-sm text-purple-400 hover:text-purple-300" title="Link to incident" aria-label="Link to incident">
            <AlertTriangle className="w-4 h-4" />
          </button>
          <button onClick={onLink} className="btn-icon btn-icon-sm text-blue-400 hover:text-blue-300" title="Link to another item" aria-label="Link to another item">
            <Link2 className="w-4 h-4" />
          </button>
          {canDelete ? (
            <button onClick={() => onDelete(id)} className="btn-icon btn-icon-sm text-red-400 hover:text-red-300" title="Delete Entry" aria-label="Delete Entry">
              <Trash2 className="w-4 h-4" />
            </button>
          ) : (
            <div className="btn-icon btn-icon-sm" title="You do not have permission to delete entries">
              <Lock className="w-4 h-4 text-glass-400" />
            </div>
          )}
        </div>
      </div>
      <p className="subcard p-3 text-sm text-white">{content}</p>
      <TicketLinks itemId={id} itemType={type} />
      <EvidenceSection itemId={id} itemType={type} />
    </div>
  );
};

export interface PlayerDataViewProps {
  openSearchModalWithIp: (query: string) => void;
  user: Identity | null;
}

const PlayerStatsSkeleton = () => (
  <div className="glass-card p-4 md:p-5 md:col-span-2 lg:col-span-1" aria-busy="true">
    <div className="flex items-center gap-3 mb-4">
      <SkeletonLoader className="w-9 h-9 rounded-lg" />
      <div className="space-y-2">
        <SkeletonLoader className="h-4 w-24" />
        <SkeletonLoader className="h-3 w-32" />
      </div>
    </div>
    <div className="space-y-3">
      <SkeletonLoader className="h-3 w-full" />
      <SkeletonLoader className="h-3 w-5/6" />
      <SkeletonLoader className="h-3 w-full" />
      <SkeletonLoader className="h-3 w-4/6" />
      <SkeletonLoader className="h-3 w-full" />
    </div>
  </div>
);

export const PlayerDataView = ({ openSearchModalWithIp, user }: PlayerDataViewProps) => {
  const { uuid: routeUuid } = useParams<{ uuid: string }>();
  const isMobile = useIsMobile();
  const [playerData, setPlayerData] = useState<PlayerData | null>(null)
  const [playerDataKey, setPlayerDataKey] = useState<string | null>(null)
  const [playerStats, setPlayerStats] = useState<PlayerStats | null>(null)
  const [relationshipIdentity, setRelationshipIdentity] = useState<{ routeId: string; uuid: string } | null>(null);
  const [itemSpawnData, setItemSpawnData] = useState<ItemSpawnData | null>(null)
  const [itemSpawnDataKey, setItemSpawnDataKey] = useState<string | null>(null)
  const [effectSpawnData, setEffectSpawnData] = useState<EffectSpawnData | null>(null)
  const [effectSpawnDataKey, setEffectSpawnDataKey] = useState<string | null>(null)
  const [creativeLogData, setCreativeLogData] = useState<CreativeLogData | null>(null)
  const [creativeLogDataKey, setCreativeLogDataKey] = useState<string | null>(null)
  const [playerEconomyLog, setPlayerEconomyLog] = useState<EconomyLogData | null>();
  const [playerEconomyLogKey, setPlayerEconomyLogKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { status: freshness, load: loadRedis } = useSwrLoader()
  const [activeTab, setActiveTab] = useState('overview')
  const [rawJsonResponse, setRawJsonResponse] = useState<string | null>(null)
  const [isDebugVisible, setIsDebugVisible] = useState(false)
  const [isIpVisible, setIsIpVisible] = useState(false)
  const [notes, setNotes] = useState<NoteWarning[]>([]);
  const [warnings, setWarnings] = useState<NoteWarning[]>([]);
  const [showAutoMod, setShowAutoMod] = useState(false);
  const [bans, setBans] = useState<Ban[]>([]);
  const [modalState, setModalState] = useState<{ type: 'note' | 'warning' | 'ban' | 'link' | 'incident' | null }>({ type: null });
  const [itemToLink, setItemToLink] = useState<{ id: string, type: 'note' | 'warning' | 'ban' } | null>(null);
  const [isIncidentLinkModalOpen, setIsIncidentLinkModalOpen] = useState(false);
  const [copiedHome, setCopiedHome] = useState<string | null>(null);
  const [collapsedServers, setCollapsedServers] = useState<Set<string>>(new Set());
  const [homeSearchQueries, setHomeSearchQueries] = useState<Record<string, string>>({});
  const [compensationLogs, setCompensationLogs] = useState<CompensationLogHit[]>([]);
  const [compensationLoading, setCompensationLoading] = useState(false);
  const [compensationTotal, setCompensationTotal] = useState(0);
  const [compensationFetched, setCompensationFetched] = useState(false);

  const { addTab } = useChatTabs();
  const navigate = useNavigate();
  useDocumentTitle(routeUuid ? `Player: ${playerStats?.username || routeUuid}` : 'Player Data');

  const canCreateNoteWarning = useMemo(() => user?.raw.custom.groups.includes('SPY_Notes') || user?.raw.custom.groups.includes('SPY_Notes_Editor') || false, [user]);
  const canDeleteNoteWarning = useMemo(() => user?.raw.custom.groups.includes('SPY_Notes_Editor') || false, [user]);
  const canExploreRelationships = canViewRelationships(user);
  const canReadLogbook = canViewLogbook(user);
  // A profile route may contain a username; only pass its resolved UUID to the graph.
  const relationshipPlayerUuid = relationshipIdentity && relationshipIdentity.routeId === routeUuid ? relationshipIdentity.uuid : null;
  useEffect(() => {
    if (!canExploreRelationships && activeTab === 'relationships') setActiveTab('overview');
    if (!canReadLogbook && activeTab === 'logbook') setActiveTab('overview');
  }, [canExploreRelationships, canReadLogbook, activeTab]);
  // Staff Manager Notes (confidential; argus.staff.notes.*). Shown when the player is staff or already has notes.
  const staffPerms = useMemo(() => getStaffPerms(user ?? null, import.meta.env.DEV), [user]);
  const playerStaffTeams = useStaffTeams(staffPerms.canReadNotes ? routeUuid : null);
  const canBan = useMemo(() => {
    if (!user) return false;
    const groups = user.raw.custom.groups;
    return groups.includes('SPY_Bans_Helper') || groups.includes('SPY_Bans_Mod') || groups.includes('SPY_Bans_Admin') || groups.includes('SPY_Bans_Superadmins');
  }, [user]);

  const canManageBans = useMemo(() => {
    if (!user) return false;
    const groups = user.raw.custom.groups;
    return groups.includes('SPY_Bans_Admin') || groups.includes('SPY_Bans_Superadmins') || groups.includes('SPY_Bans_Superadmin');
  }, [user]);

  const canDeleteBans = useMemo(() => {
    if (!user) return false;
    const groups = user.raw.custom.groups;
    return groups.includes('SPY_Bans_Superadmins') || groups.includes('SPY_Bans_Superadmin');
  }, [user]);

  const canViewAppeals = useMemo(() => {
    if (!user) return false;
    const groups = user.raw.custom.groups;
    return groups.includes('SPY_Bans_Helper') || groups.includes('SPY_Bans_Mod') || groups.includes('SPY_Bans_Admin') || groups.includes('SPY_Bans_Superadmins') || groups.includes('SPY_Super_admin');
  }, [user]);

  const isAdminOrSuperAdmin = useMemo(() => {
    if (!user) return false;
    const groups = user.raw.custom.groups;
    return groups.includes('SPY_Bans_Admin') || groups.includes('SPY_Bans_Superadmins');
  }, [user]);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const openChatForPlayer = () => {
    if (!routeUuid || !playerData) return;
    const name = playerStats?.username || playerData.lastKnownName;
    if (!name) return;
    addTab(
        { userSearch: [name] },
        `${name}'s Chat`
    );
    navigate('/chat');
};

const isPreciseIpSearch = (query: string) => {
  // Match 3 octets and at least one digit of the 4th
  const ipRegex = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d+/;
  return ipRegex.test(query);
};

useEffect(() => {
  const searchPlayers = async () => {
    if (debouncedSearchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const results = await elasticsearchService.searchPlayers(debouncedSearchQuery);
      setSearchResults(results.hits.hits);
    } catch (error) {
      console.error("Failed to search players:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  if (!routeUuid) {
    searchPlayers();
  }
}, [debouncedSearchQuery, routeUuid]);

const handleUsernamesUpdated = useCallback(() => {
  searchResults.forEach(result => {
    if (!result._source.lastKnownName) {
      const newName = usernameCacheService.getName(result._source.uuid);
      if (newName) {
        elasticsearchService.updatePlayerName(result._id, newName)
          .catch(err => console.error(`Failed to update name for ${result._source.uuid}:`, err));
      }
    }
  });
}, [searchResults]);

useEffect(() => {
  if (searchResults.length > 0) {
    const uuids = searchResults.map((hit: any) => hit._source.uuid);
    usernameCacheService.prefetchUsernames(uuids);
  }

  document.addEventListener('usernamesUpdated', handleUsernamesUpdated);
  return () => {
    document.removeEventListener('usernamesUpdated', handleUsernamesUpdated);
  };
}, [searchResults, handleUsernamesUpdated]);

  const handleBanDeleted = (banUuid: string) => {
    setBans(prev => prev.filter(b => b.ban_uuid !== banUuid));
  };

  const handleBanUpdated = (updatedBan: Ban) => {
    setBans(prevBans => prevBans.map(b => b.ban_uuid === updatedBan.ban_uuid ? updatedBan : b));
  };

  const handleModalSuccess = () => {
    // Re-fetch data to show the new entry
    if (routeUuid) {
      fetchData(routeUuid);
    }
  };

  const isUUID = (str: string) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
  }

  const fetchData = async (searchId: string, force = false) => {
    if (!searchId) return
    
    let superseded = false
    setLoading(true)
    setError(null)
    setRawJsonResponse(null)
    setPlayerData(null)
    setPlayerDataKey(null)
    setPlayerStats(null)
    setRelationshipIdentity(null);
    setCreativeLogData(null)
    setCreativeLogDataKey(null)
    setItemSpawnData(null)
    setItemSpawnDataKey(null)
    setEffectSpawnData(null)
    setEffectSpawnDataKey(null)
    setPlayerEconomyLog(null)
    setPlayerEconomyLogKey(null)
    setCompensationLogs([])
    setCompensationTotal(0)
    setCompensationFetched(false)
    setIsIpVisible(false)
    setShowAutoMod(false)
    
    try {
      let resolvedUuid = searchId;
      if (!isUUID(resolvedUuid)) {
        // It's a username, so fetch stats to get the UUID
        const statsResponse = await usersApiService.getPlayerStatsByUsername(resolvedUuid);
        setPlayerStats(statsResponse.player_stats);
        resolvedUuid = statsResponse.player_stats.uuid;
      }

      const compactUuid = resolvedUuid.replace(/-/g, '').toLowerCase();
      if (/^[0-9a-f]{32}$/.test(compactUuid)) {
        setRelationshipIdentity({
          routeId: searchId,
          uuid: `${compactUuid.slice(0, 8)}-${compactUuid.slice(8, 12)}-${compactUuid.slice(12, 16)}-${compactUuid.slice(16, 20)}-${compactUuid.slice(20)}`,
        });
      }
      
      // Cache, then recache on load: the last copies of the five Redis reads paint at once (if all
      // five exist), Redis is re-read every time, and the fresh answers replace them. The
      // freshness badge in the header says which is on screen.
      const got: {
        player?: { data: PlayerData; key: string }
        itemSpawns?: { data: ItemSpawnData | null; key: string }
        effectSpawns?: { data: EffectSpawnData | null; key: string }
        creativeLog?: { data: CreativeLogData | null; key: string }
        economyLog?: { data: EconomyLogData | null; key: string }
      } = {}
      const cacheId = resolvedUuid.replace(/-/g, '').toLowerCase()
      const redis = await loadRedis(
        [
          read(`redis:player:${cacheId}`, () => gameRedisService.getPlayerData(resolvedUuid), (r) => { got.player = r; setPlayerData(r.data); setPlayerDataKey(r.key) }),
          read(`redis:player:${cacheId}:item-spawns`, () => gameRedisService.getItemSpawnData(resolvedUuid), (r) => { got.itemSpawns = r; setItemSpawnData(r.data); setItemSpawnDataKey(r.key) }),
          read(`redis:player:${cacheId}:effect-spawns`, () => gameRedisService.getEffectSpawnData(resolvedUuid), (r) => { got.effectSpawns = r; setEffectSpawnData(r.data); setEffectSpawnDataKey(r.key) }),
          read(`redis:player:${cacheId}:creative-log`, () => gameRedisService.getCreativeLog(resolvedUuid), (r) => { got.creativeLog = r; setCreativeLogData(r.data); setCreativeLogDataKey(r.key) }),
          read(`redis:player:${cacheId}:economy-log`, () => gameRedisService.getPlayerEconomyLog(resolvedUuid), (r) => { got.economyLog = r; setPlayerEconomyLog(r.data); setPlayerEconomyLogKey(r.key) }),
        ],
        { force, onCached: () => setLoading(false) }
      )
      superseded = redis.superseded
      if (superseded) return
      // 'stale' keeps the cached copies on screen and the badge reports the failed re-read; only a
      // failure with nothing to show (or a definitive "not found") is an error here.
      if (redis.status.phase === 'error') throw new Error(redis.status.error ?? 'Failed to fetch player data')
      
      if (resolvedUuid) {
        const [notesRes, warningsRes, bansRes] = await Promise.all([
          notesApiService.getNotesForPlayer(resolvedUuid),
          notesApiService.getWarningsForPlayer(resolvedUuid),
          notesApiService.getBansForUser(resolvedUuid),
        ]);
        setNotes(notesRes);
        setWarnings(warningsRes);
        setBans(bansRes);
      }

      // If we looked up by username, we already have stats. If by UUID, fetch them now.
      if (!playerStats) {
        const statsResponse = await usersApiService.getPlayerStatsByUuid(resolvedUuid);
        setPlayerStats(statsResponse.player_stats);
      }
      
      setRawJsonResponse(JSON.stringify({ 
        playerData: got.player?.data ?? null, 
        playerDataKey: got.player?.key ?? null,
        playerStats,
        itemSpawnData: got.itemSpawns?.data ?? null,
        itemSpawnDataKey: got.itemSpawns?.key ?? null,
        effectSpawnData: got.effectSpawns?.data ?? null,
        effectSpawnDataKey: got.effectSpawns?.key ?? null,
        creativeLogData: got.creativeLog?.data ?? null,
        creativeLogDataKey: got.creativeLog?.key ?? null,
        playerEconomyLog: got.economyLog?.data ?? null,
        playerEconomyLogKey: got.economyLog?.key ?? null
      }, null, 2))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch player data')
      setPlayerData(null)
      setPlayerStats(null)
    } finally {
      if (!superseded) setLoading(false)
    }
  }

  useEffect(() => {
    if (routeUuid) {
      fetchData(routeUuid)
    }
  }, [routeUuid])

  useEffect(() => {
    if (activeTab !== 'compensation' || !routeUuid || compensationFetched || compensationLoading) return;

    const fetchCompensationLogs = async () => {
      setCompensationLoading(true);
      try {
        const query = {
          size: 200,
          sort: [{ timestamp: { order: 'desc' } }],
          query: {
            term: { player_uuid: routeUuid }
          }
        };
        const response = await elasticsearchService.getCompensationLogs(query);
        const totalValue = typeof response.hits.total === 'object'
          ? response.hits.total.value
          : response.hits.total;
        setCompensationLogs(response.hits.hits);
        setCompensationTotal(totalValue ?? 0);
      } catch (err) {
        console.error('Failed to fetch compensation logs:', err);
      } finally {
        setCompensationLoading(false);
        setCompensationFetched(true);
      }
    };

    fetchCompensationLogs();
  }, [activeTab, routeUuid, compensationFetched, compensationLoading]);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: User },
    ...(canExploreRelationships ? [{ id: 'relationships', label: 'Relationships', icon: Network }] : []),
    { id: 'permissions', label: 'Permissions', icon: Shield },
    { id: 'homes', label: 'Homes', icon: Home },
    { id: 'ranks', label: 'Ranks', icon: Crown },
    { id: 'economy', label: 'Economy', icon: DollarSign },
    { id: 'chat', label: 'Chat Settings', icon: MessageSquare },
    { id: 'activity', label: 'Activity', icon: Activity },
    { id: 'logs', label: 'Logs', icon: List },
    ...(canReadLogbook ? [{ id: 'logbook', label: 'Logbook', icon: ScrollText }] : []),
    { id: 'notes', label: 'Notes & Warnings', icon: FileText },
    { id: 'bans', label: 'Bans', icon: Axe },
    { id: 'compensation', label: 'Compensation', icon: HandCoins }
  ]

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return dateString
    }
  }

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
  }

  const handleClearRankCache = (server: string) => {
    console.log(`TODO: Clear rank cache for player ${playerStats?.uuid} on server ${server}`);
    // TODO: After clearing the cache via an API call, send a pub/sub message
    // to the backend to trigger a rank renewal for the player.
    // The specific message format and pub/sub channel will need to be implemented.
    // Example:
    // pubsub.publish('quasar:rank-update', JSON.stringify({
    //   userId: playerStats?.uuid,
    //   action: 'RENEW_RANKS',
    //   server: server
    // }));
  };

  const renderNotesAndWarnings = () => {
    const handleDelete = async (id: string) => {
      if (window.confirm('Are you sure you want to delete this entry?')) {
        try {
          await notesApiService.deleteEntry(id);
          setNotes(prev => prev.filter(entry => entry.id !== id));
          setWarnings(prev => prev.filter(entry => entry.id !== id));
        } catch (err) {
          alert('Failed to delete entry. You may not have the required permissions.');
        }
      }
    };

    const autoModHiddenCount = countAutoModEntries([...notes, ...warnings]);
    const visibleWarnings = filterAutoModEntries(warnings, showAutoMod);
    const visibleNotes = filterAutoModEntries(notes, showAutoMod);
    
    return (
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-white flex items-center gap-2"><FileText className="w-4 h-4 text-accent-300" />Notes & Warnings</h2>
            <p className="text-xs text-glass-400 mt-0.5">Internal staff notes and warnings for this player.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <ShowAutoModToggle
              checked={showAutoMod}
              onChange={setShowAutoMod}
              hiddenCount={autoModHiddenCount}
            />
            {canCreateNoteWarning ? (
              <>
                <button onClick={() => setModalState({ type: 'note' })} className="glass-button-sm"><FileText className="w-3.5 h-3.5" />Create Note</button>
                <button onClick={() => setModalState({ type: 'warning' })} className="glass-button-sm bg-yellow-500/10 text-yellow-300 hover:bg-yellow-500/15"><TriangleAlert className="w-3.5 h-3.5" />Create Warning</button>
              </>
            ) : (
              <div className="flex items-center gap-1.5 text-xs text-glass-400">
                <Lock className="w-3.5 h-3.5" />
                <span>No creation permissions</span>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Warnings */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-white flex items-center gap-2"><TriangleAlert className="w-4 h-4 text-yellow-400" /> Warnings <span className="badge badge-neutral">{visibleWarnings.length}{!showAutoMod && warnings.length !== visibleWarnings.length ? ` / ${warnings.length}` : ''}</span></h4>
            {visibleWarnings.map(entry => (
              <NoteWarningCard key={entry.id} entry={entry} onDelete={handleDelete} canDelete={canDeleteNoteWarning} onLink={() => { setItemToLink({ id: entry.id, type: entry.type }); setModalState({ type: 'link' }); }} onLinkIncident={() => { setItemToLink({ id: entry.id, type: entry.type }); setIsIncidentLinkModalOpen(true); }} />
            ))}
            {visibleWarnings.length === 0 && (
              <EmptyState compact icon={TriangleAlert} title="No warnings" description={warnings.length > 0 && !showAutoMod ? 'No warnings shown. Enable Show AutoMod to view hidden entries.' : 'No warnings for this player.'} />
            )}
          </div>

          {/* Notes */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-white flex items-center gap-2"><FileText className="w-4 h-4 text-blue-400" /> Notes <span className="badge badge-neutral">{visibleNotes.length}{!showAutoMod && notes.length !== visibleNotes.length ? ` / ${notes.length}` : ''}</span></h4>
            {visibleNotes.map(entry => (
              <NoteWarningCard key={entry.id} entry={entry} onDelete={handleDelete} canDelete={canDeleteNoteWarning} onLink={() => { setItemToLink({ id: entry.id, type: entry.type }); setModalState({ type: 'link' }); }} onLinkIncident={() => { setItemToLink({ id: entry.id, type: entry.type }); setIsIncidentLinkModalOpen(true); }} />
            ))}
            {visibleNotes.length === 0 && (
              <EmptyState compact icon={FileText} title="No notes" description={notes.length > 0 && !showAutoMod ? 'No notes shown. Enable Show AutoMod to view hidden entries.' : 'No notes for this player.'} />
            )}
          </div>
        </div>

        {staffPerms.canReadNotes && routeUuid && (
          <StaffNotesPanel
            subject={{ uuid: routeUuid, display_name: playerStats?.username ?? undefined }}
            canWrite={staffPerms.canWriteNotes}
            framed
            hideWhenEmpty={playerStaffTeams.length === 0}
          />
        )}
      </div>
    )
  };

  const renderOverview = () => {
    const nicknameStyle: React.CSSProperties = {}
    if (playerData?.nicknameColors && playerData.nicknameColors.length > 0) {
      if (playerData.nicknameColors.length > 1) {
        nicknameStyle.backgroundImage = `linear-gradient(to right, ${playerData.nicknameColors.join(', ')})`
        nicknameStyle.backgroundClip = 'text'
        nicknameStyle.WebkitBackgroundClip = 'text'
        nicknameStyle.color = 'transparent'
        nicknameStyle.WebkitTextFillColor = 'transparent'
        nicknameStyle.fontWeight = '600'
      } else {
        nicknameStyle.color = playerData.nicknameColors[0]
      }
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Basic Info */}
        <div className="glass-card p-4 md:p-5">
          <div className="flex flex-col items-center text-center pb-4 mb-1 border-b border-glass-200/10">
            {playerStats?.uuid && (
              <img
                src={`/cdu-heads/head/${playerStats.uuid}/100.png`}
                alt="Player Avatar"
                className="w-20 h-20 mb-3 rounded-md"
              />
            )}
            <h3 className="font-semibold text-white text-lg tracking-tight" style={nicknameStyle}>
              {playerStats?.username || playerData?.nickname}
            </h3>
            <p className="text-xs text-glass-400 font-mono break-all">{playerStats?.uuid}</p>
          </div>

          <div className="divide-y divide-glass-200/10">
            <InfoRow label="Last Known Name">{playerData?.lastKnownName}</InfoRow>
            <InfoRow label="Prefix"><span className="font-mono text-xs">{playerData?.prefix}</span></InfoRow>
            <InfoRow label="Vote Count">{playerData?.voteCount}</InfoRow>
          </div>
        </div>

        {/* Status */}
        <div className="glass-card p-4 md:p-5">
          <SectionHeader title="Status" description="Current state" icon={Activity} iconClassName="text-green-400" />
          <div className="divide-y divide-glass-200/10">
            <InfoRow label="Banned">
              <span className={cn(
                "badge badge-dot",
                playerStats?.banned ? "badge-danger" : "badge-success"
              )}>
                {playerStats?.banned ? 'Yes' : 'No'}
              </span>
            </InfoRow>
            <InfoRow label="Muted">
              <span className={cn(
                "badge badge-dot",
                playerData?.muted ? "badge-danger" : "badge-success"
              )}>
                {playerData?.muted ? 'Yes' : 'No'}
              </span>
            </InfoRow>
            <InfoRow label="PVP">
              <span className={cn(
                "badge badge-dot",
                playerData?.pvp ? "badge-success" : "badge-danger"
              )}>
                {playerData?.pvp ? 'Enabled' : 'Disabled'}
              </span>
            </InfoRow>
            <InfoRow label="Prefix Effect">
              <span className={cn(
                "badge badge-dot",
                playerData?.prefixEffectEnabled ? "badge-success" : "badge-danger"
              )}>
                {playerData?.prefixEffectEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </InfoRow>
          </div>
        </div>

        {/* Chat Settings */}
        <div className="glass-card p-4 md:p-5">
          <SectionHeader title="Chat Settings" description="Communication preferences" icon={MessageSquare} iconClassName="text-purple-400" />
          <div className="divide-y divide-glass-200/10">
            <InfoRow label="Universal Chat">
              <span className={cn(
                "badge badge-dot",
                playerData?.universalChatEnabled ? "badge-success" : "badge-danger"
              )}>
                {playerData?.universalChatEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </InfoRow>
            <InfoRow label="Game Messages">
              <span className={cn(
                "badge badge-dot",
                playerData?.gameMessagesEnabled ? "badge-success" : "badge-danger"
              )}>
                {playerData?.gameMessagesEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </InfoRow>
            <InfoRow label="Channel">{playerData?.chatChannel.displayName}</InfoRow>
          </div>
        </div>

        {/* Player Stats */}
        {playerData && !playerStats ? <PlayerStatsSkeleton /> : null}
        {playerStats && (
          <div className="glass-card p-4 md:p-5 md:col-span-2 lg:col-span-1">
            <SectionHeader title="Player Stats" description="From Users API" icon={User} iconClassName="text-yellow-400" />
            <div className="divide-y divide-glass-200/10">
              <InfoRow label="Playtime">{playerStats.playtime}</InfoRow>
              <InfoRow label="Deaths">{playerStats.mc_deaths}</InfoRow>
              <InfoRow label="Jumps">{playerStats.mc_jumps}</InfoRow>
              <InfoRow label="Blocks Broken">{playerStats.mc_blocksbroken}</InfoRow>
              <InfoRow label="Discord Booster">
                <span className={cn(
                  "badge",
                  playerStats.isserverbooster ? "badge-purple" : "badge-neutral"
                )}>
                  {playerStats.isserverbooster ? 'Yes' : 'No'}
                </span>
              </InfoRow>
            </div>
          </div>
        )}
        {canExploreRelationships && relationshipPlayerUuid && (
          <div className="glass-card p-4 md:p-5">
            <SectionHeader title="Relationships" icon={Network} />
            <p className="text-sm text-glass-400 mb-4">
              Explore likely groups, regular connections and the activity supporting them.
            </p>
            <Link
              to={`/relationships?player=${encodeURIComponent(relationshipPlayerUuid)}`}
              className="glass-button"
            >
              Explore relationships <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    )
  }

  const renderPermissions = () => (
    <div className="space-y-6">
      <div className="glass-card p-4 md:p-5">
        <SectionHeader
          title="Permissions"
          icon={Shield}
          count={playerData?.permissions?.length ?? 0}
          actions={<RedisKeyInfo redisKey={playerDataKey} />}
        />
        {playerData?.permissions && playerData.permissions.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {playerData.permissions.map((permission, index) => (
              <div key={index} className="subcard px-3 py-2 min-w-0">
                <span className="text-white font-mono text-xs break-all">{permission}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-glass-400">No permissions set.</p>
        )}
      </div>

      <div className="glass-card p-4 md:p-5">
        <SectionHeader
          title="Tags by Server"
          icon={Tag}
          iconClassName="text-purple-400"
          actions={<RedisKeyInfo redisKey={playerDataKey} />}
        />
        <div className="space-y-3">
          {Object.entries(playerData?.tags || {}).map(([server, tags]) => (
            <div key={server} className="subcard p-3">
              <h4 className="text-sm font-medium text-white mb-2 break-all">{server}</h4>
              <div className="flex flex-wrap gap-1.5">
                {tags.length > 0 ? (
                  tags.map((tag, index) => (
                    <span key={index} className="badge badge-purple">
                      {tag}
                    </span>
                  ))
                ) : (
                  <span className="text-glass-500 text-xs">No tags</span>
                )}
              </div>
            </div>
          ))}
          {Object.keys(playerData?.tags || {}).length === 0 && (
            <p className="text-sm text-glass-400">No tags on any server.</p>
          )}
        </div>
      </div>
    </div>
  )

  const renderHomes = () => {
    const homesEntries = Object.entries(playerData?.homes || {});
    const serversWithHomes = homesEntries.filter(([, homes]) => Object.keys(homes).length > 0);
    const serversWithoutHomes = homesEntries.filter(([, homes]) => Object.keys(homes).length === 0);
    
    const handleCopyTpCommand = (coordinates: string) => {
        const normalizedCoords = coordinates.replace(/,/g, ' ').replace(/\s+/g, ' ').trim();
        const parts = normalizedCoords.split(' ');

        let tpCommand: string;

        if (parts.length >= 4 && isNaN(parseFloat(parts[0]))) {
            const dimension = parts[0];
            const x = parts[1];
            const y = parts[2];
            const z = parts[3];
            tpCommand = `/execute in ${dimension} run tp @s ${x} ${y} ${z}`;
        } else {
            const coords = parts.join(' ');
            tpCommand = `/tp @s ${coords}`;
        }

        navigator.clipboard.writeText(tpCommand).then(() => {
            setCopiedHome(coordinates);
            setTimeout(() => setCopiedHome(null), 2000);
        });
    };

    const toggleServerCollapse = (server: string) => {
      setCollapsedServers(prev => {
        const newSet = new Set(prev);
        if (newSet.has(server)) {
          newSet.delete(server);
        } else {
          newSet.add(server);
        }
        return newSet;
      });
    };

    const updateHomeSearch = (server: string, query: string) => {
      setHomeSearchQueries(prev => ({
        ...prev,
        [server]: query
      }));
    };

    const filterHomes = (homes: Record<string, string>, server: string) => {
      const searchQuery = homeSearchQueries[server]?.toLowerCase() || '';
      if (!searchQuery) return homes;
      
      return Object.fromEntries(
        Object.entries(homes).filter(([homeName]) => 
          homeName.toLowerCase().includes(searchQuery)
        )
      );
    };

    return (
      <div className="space-y-6">
        <div>
          <SectionHeader
            as="h2"
            title="Servers with Homes"
            icon={Home}
            iconClassName="text-green-400"
            count={serversWithHomes.length}
            actions={<RedisKeyInfo redisKey={playerDataKey} />}
          />
          {serversWithHomes.length > 0 ? (
            <div className="space-y-4">
              {serversWithHomes.map(([server, homes]) => {
                const isCollapsed = collapsedServers.has(server);
                const homeCount = Object.keys(homes).length;
                const filteredHomes = filterHomes(homes, server);
                const filteredCount = Object.keys(filteredHomes).length;
                const showSearch = homeCount > 5; // Show search if more than 5 homes

                return (
                  <div key={server} className="glass-card p-4 md:p-5">
                    <div className={cn("flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2", !isCollapsed && "mb-3")}>
                      <button
                        onClick={() => toggleServerCollapse(server)}
                        className="flex items-center gap-2 text-white hover:text-accent-300 transition-colors min-w-0 text-left"
                        aria-expanded={!isCollapsed}
                      >
                        <Home className="w-4 h-4 text-green-400 shrink-0" />
                        <span className="text-sm font-semibold truncate">{server}</span>
                        <span className="badge badge-neutral">{homeCount} homes</span>
                        {isCollapsed ? (
                          <ChevronDown className="w-4 h-4 text-glass-400 shrink-0" />
                        ) : (
                          <ChevronUp className="w-4 h-4 text-glass-400 shrink-0" />
                        )}
                      </button>
                      {!isCollapsed && showSearch && (
                        <div className="flex items-center gap-2">
                          <div className="relative flex-1 sm:flex-none">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
                            <input
                              type="text"
                              placeholder="Search homes..."
                              value={homeSearchQueries[server] || ''}
                              onChange={(e) => updateHomeSearch(server, e.target.value)}
                              className="glass-input pl-9 w-full sm:w-56"
                              aria-label={`Search homes on ${server}`}
                            />
                          </div>
                          {homeSearchQueries[server] && (
                            <span className="text-xs text-glass-400 tabular-nums whitespace-nowrap">
                              {filteredCount} of {homeCount}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {!isCollapsed && (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {Object.entries(filteredHomes).map(([homeName, coordinates]) => (
                          <div key={homeName} className="subcard p-3 min-w-0">
                            <div className="flex items-center justify-between gap-2 mb-1">
                                <div className="flex items-center gap-2 min-w-0">
                                    <MapPin className="w-4 h-4 text-green-400 shrink-0" />
                                    <span className="text-sm text-white font-medium truncate">{homeName}</span>
                                </div>
                                <button
                                  onClick={() => handleCopyTpCommand(coordinates)}
                                  className="btn-icon btn-icon-sm"
                                  title={copiedHome === coordinates ? 'Copied!' : 'Copy teleport command'}
                                  aria-label="Copy teleport command"
                                >
                                    {copiedHome === coordinates ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                                </button>
                            </div>
                            <p className="text-glass-300 text-xs font-mono tabular-nums break-all">{coordinates}</p>
                          </div>
                        ))}
                        {filteredCount === 0 && homeSearchQueries[server] && (
                          <div className="col-span-full text-center py-4">
                            <p className="text-sm text-glass-400">No homes found matching "{homeSearchQueries[server]}"</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState icon={Home} title="No homes set" description="Player has not set any homes." />
          )}
        </div>

        {serversWithoutHomes.length > 0 && (
          <div>
            <SectionHeader as="h2" title="Visited Servers (No Homes)" icon={Globe} count={serversWithoutHomes.length} />
            <div className="glass-card p-4 md:p-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {serversWithoutHomes.map(([server]) => (
                  <div key={server} className="subcard flex items-center gap-2 px-3 py-2 min-w-0">
                    <Home className="w-4 h-4 text-glass-400 shrink-0" />
                    <span className="text-white text-sm truncate">{server}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  /**
   * Renders the player's manual ranks.
   * 
   * A note on manual ranks from the API:
   * Ranks can have modifiers to specify their scope and expiration.
   * Modifiers are enclosed in parentheses and can be combined.
   * 
   * Formats:
   * - `rankName(target:scope:targetId)`: Applies rank to a specific scope (e.g., a cluster).
   * - `rankName(expires:unix_timestamp)`: Sets an expiration date for the rank.
   * - `rankName(target:cluster:BCG)(expires:1672531199)`: A combination of modifiers.
   * 
   * If no modifiers are present, the rank is network-wide and does not expire.
   */
  const renderRanks = () => (
    <div className="space-y-6">
      <div className="glass-card p-4 md:p-5">
        <SectionHeader
          title="Manual Ranks"
          icon={Crown}
          iconClassName="text-yellow-400"
          count={playerData?.manualRanks?.length ?? 0}
          actions={<RedisKeyInfo redisKey={playerDataKey} />}
        />
        <div className="flex flex-wrap gap-3">
          {playerData?.manualRanks.map((rankString, index) => {
            const nameMatch = rankString.match(/^([\w-]+)/);
            const name = nameMatch ? nameMatch[1] : rankString;
            
            const modifiers: { [key: string]: string } = {};
            const modifierRegex = /\(([^)]+)\)/g;
            let match;
            while ((match = modifierRegex.exec(rankString)) !== null) {
              const parts = match[1].split(':');
              const key = parts.shift();
              const value = parts.join(':');
              if (key) {
                modifiers[key] = value;
              }
            }

            let target: string | null = null;
            if (modifiers.target) {
              const targetParts = modifiers.target.split(':');
              target = targetParts[1];
            }

            let expiresDate: Date | null = null;
            if (modifiers.expires) {
              const expiresValue = modifiers.expires;
              if (/^\d+$/.test(expiresValue)) {
                expiresDate = new Date(parseInt(expiresValue, 10) * 1000);
              } else {
                expiresDate = new Date(expiresValue.replace(' ', 'T'));
              }
              if (isNaN(expiresDate.getTime())) {
                expiresDate = null;
              }
            }
            const isExpired = expiresDate ? expiresDate < new Date() : false;

            return (
              <div key={index} className={cn(
                "subcard p-3 flex flex-col justify-between w-full sm:w-48 h-24",
                isExpired && "opacity-60 border-red-500/20"
              )}>
                <div>
                  <span className={cn(
                    "font-semibold text-base", 
                    isExpired ? "text-red-300 line-through" : "text-yellow-300"
                  )}>
                    {name}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {target ? (
                    <span className={cn("badge", isExpired ? "badge-danger" : "badge-warning")} title="Scope">
                      <Globe2 className="w-3 h-3" />
                      <span>{target}</span>
                    </span>
                  ) : (
                    <span className={cn("badge", isExpired ? "badge-danger" : "badge-warning")} title="Scope">
                      <Globe2 className="w-3 h-3" />
                      <span>Network</span>
                    </span>
                  )}
                  {expiresDate && (
                    <span className={cn("badge", isExpired ? "badge-danger" : "badge-warning")} title={isExpired ? 'Expired' : 'Expires'}>
                      <Clock className="w-3 h-3" />
                      <span>{expiresDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                    </span>
                  )}
                </div>
              </div>
            );
          })}
          {(!playerData?.manualRanks || playerData.manualRanks.length === 0) && (
            <p className="text-sm text-glass-400">No manual ranks.</p>
          )}
        </div>
      </div>

      <div className="glass-card p-4 md:p-5">
        <SectionHeader
          title="Processed Rank Cache"
          icon={Activity}
          iconClassName="text-blue-400"
          actions={<RedisKeyInfo redisKey={playerDataKey} />}
        />
        <div className="space-y-3">
          {Object.entries(playerData?.activeRanksCache || {}).map(([server, ranks]) => (
            <div key={server} className="subcard p-3">
              <div className="flex justify-between items-center gap-2 mb-2">
                <h4 className="text-sm font-medium text-white truncate">{server}</h4>
                <button 
                  onClick={() => handleClearRankCache(server)}
                  className="btn-icon btn-icon-sm text-red-400 hover:text-red-300" 
                  title="Clear rank cache for this server"
                  aria-label="Clear rank cache for this server"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {ranks.map((rank, index) => (
                  <span key={index} className="badge badge-info">
                    {rank}
                  </span>
                ))}
              </div>
            </div>
          ))}
          {Object.keys(playerData?.activeRanksCache || {}).length === 0 && (
            <p className="text-sm text-glass-400">No cached ranks.</p>
          )}
        </div>
      </div>
    </div>
  )

  const renderEconomy = () => (
    <section>
      <SectionHeader
        as="h2"
        title="Balance by Cluster"
        icon={DollarSign}
        iconClassName="text-green-400"
        actions={<RedisKeyInfo redisKey={playerDataKey} />}
      />
      {Object.keys(playerData?.balancePerCluster || {}).length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {Object.entries(playerData?.balancePerCluster || {}).map(([cluster, balance]) => (
            <StatTile key={cluster} label={cluster} value={`₡${balance}`} icon={DollarSign} tone="success" />
          ))}
        </div>
      ) : (
        <EmptyState compact icon={DollarSign} title="No balances" description="No balance data found for this player." />
      )}
    </section>
  )

  const renderPlayerEconomyLog = () => {
    if (!playerEconomyLog) {
      return (
        <div className="mt-6">
          <EmptyState compact icon={List} title="Player Economy Log" description="No economy log data found for this player." />
        </div>
      );
    }
  
    const reversedLog = [...playerEconomyLog.XRANGE].reverse();
  
    return (
      <div className="glass-card p-4 md:p-5 mt-6">
        <SectionHeader
          title="Transaction History"
          icon={List}
          count={reversedLog.length}
          actions={<RedisKeyInfo redisKey={playerEconomyLogKey} />}
        />
        {/* Mobile: Card list */}
        <div className="md:hidden space-y-2 max-h-96 overflow-y-auto pr-1">
          {reversedLog.map(({ id, msg }) => {
            const timestamp = new Date(parseInt(id.split('-')[0])).toLocaleString();
            return (
              <div key={id} className="subcard p-3 space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="badge badge-accent">{msg.action}</span>
                  <span className="text-sm font-mono font-semibold text-green-400 tabular-nums">₡{msg.money}</span>
                </div>
                <div className="text-sm text-glass-300">
                  {msg.itemDisplayName && <span>{msg.itemDisplayName} (x{msg.count || 1})</span>}
                  {msg.from && <span>From: <PlayerProfile uuid={msg.from} inline showUuid={false} showAvatar={true} /></span>}
                  {msg.to && <span> To: <PlayerProfile uuid={msg.to} inline showUuid={false} showAvatar={true} /></span>}
                </div>
                <div className="flex items-center justify-between text-xs text-glass-500">
                  <span className="font-mono tabular-nums">{timestamp}</span>
                  <span>{msg.clusterId}</span>
                </div>
              </div>
            );
          })}
        </div>
        {/* Desktop: Table */}
        <div className="hidden md:block subcard overflow-auto max-h-96">
          <table className="table">
            <thead>
              <tr>
                <th className="th">Timestamp</th>
                <th className="th">Cluster</th>
                <th className="th">Action</th>
                <th className="th">Details</th>
                <th className="th text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {reversedLog.map(({ id, msg }) => {
                const timestamp = new Date(parseInt(id.split('-')[0])).toLocaleString();
                return (
                  <tr key={id}>
                    <td className="td !py-2 !px-3 font-mono text-xs text-glass-400 tabular-nums whitespace-nowrap">{timestamp}</td>
                    <td className="td !py-2 !px-3 text-glass-300">{msg.clusterId}</td>
                    <td className="td !py-2 !px-3 text-white font-medium">{msg.action}</td>
                    <td className="td !py-2 !px-3 text-glass-300">
                      {msg.itemDisplayName && <span>{msg.itemDisplayName} (x{msg.count || 1})</span>}
                      {msg.from && <span>From: <PlayerProfile uuid={msg.from} inline showUuid={false} showAvatar={true} /></span>}
                      {msg.to && <span>To: <PlayerProfile uuid={msg.to} inline showUuid={false} showAvatar={true} /></span>}
                    </td>
                    <td className="td !py-2 !px-3 text-right font-mono text-green-400 tabular-nums">₡{msg.money}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderChat = () => (
    <div className="space-y-6">
      <div className="glass-card p-4 md:p-5">
        <SectionHeader
          title="Chat Configuration"
          icon={MessageSquare}
          iconClassName="text-blue-400"
          actions={<RedisKeyInfo redisKey={playerDataKey} />}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h4 className="eyebrow mb-2">Current Channel</h4>
            <div className="subcard px-3">
              <div className="divide-y divide-glass-200/10">
                <InfoRow label="Display Name">{playerData?.chatChannel.displayName}</InfoRow>
                <InfoRow label="Channel ID"><span className="font-mono text-xs">{playerData?.chatChannel.channelId}</span></InfoRow>
                <InfoRow label="Sub ID"><span className="font-mono text-xs">{playerData?.chatChannel.subId || 'None'}</span></InfoRow>
                <InfoRow label="Guild Channel">{playerData?.guildChatChannel}</InfoRow>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="eyebrow mb-2">Settings</h4>
            <div className="subcard px-3">
              <div className="divide-y divide-glass-200/10">
                <InfoRow label="Universal Chat">
                  <span className={cn(
                    "badge badge-dot",
                    playerData?.universalChatEnabled ? "badge-success" : "badge-danger"
                  )}>
                    {playerData?.universalChatEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </InfoRow>
                <InfoRow label="Game Messages">
                  <span className={cn(
                    "badge badge-dot",
                    playerData?.gameMessagesEnabled ? "badge-success" : "badge-danger"
                  )}>
                    {playerData?.gameMessagesEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </InfoRow>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="glass-card p-4 md:p-5">
        <SectionHeader
          title="Ignored Players"
          icon={User}
          iconClassName="text-red-400"
          count={playerData?.ignoredPlayers?.length ?? 0}
          actions={<RedisKeyInfo redisKey={playerDataKey} />}
        />
        <div className="flex flex-wrap gap-1.5">
          {playerData?.ignoredPlayers.length ? (
            playerData.ignoredPlayers.map((player, index) => (
              <span key={index} className="badge badge-danger font-mono">
                {player}
              </span>
            ))
          ) : (
            <span className="text-sm text-glass-400">No ignored players</span>
          )}
        </div>
      </div>
    </div>
  )

  const renderActivity = () => (
    <div className="space-y-6">
      <div className="glass-card p-4 md:p-5">
        <SectionHeader
          title="Recent Activity"
          icon={Activity}
          iconClassName="text-green-400"
          actions={<RedisKeyInfo redisKey={playerDataKey} />}
        />
        <div className="divide-y divide-glass-200/10">
          {isAdminOrSuperAdmin && (
            <div className="flex justify-between items-center gap-3 py-2 text-sm">
                <span className="text-glass-400">Last Known IP</span>
                <div className="flex items-center gap-2">
                {isIpVisible ? (
                    <span className="text-white font-mono text-xs">{playerData?.lastKnownIp}</span>
                ) : (
                    <button onClick={() => setIsIpVisible(true)} className="glass-button-sm">
                    Click to reveal
                    </button>
                )}
                {isIpVisible && playerData?.lastKnownIp && (
                    <button 
                    onClick={() => openSearchModalWithIp(playerData.lastKnownIp)}
                    className="btn-icon btn-icon-sm" 
                    title="Find other players with this IP"
                    aria-label="Find other players with this IP"
                    >
                    <UsersIcon className="w-4 h-4" />
                    </button>
                )}
                </div>
            </div>
          )}
          <InfoRow label="Vote Count">{playerData?.voteCount}</InfoRow>
          <div className="flex justify-between items-center gap-3 py-2 text-sm">
            <span className="text-glass-400">Nickname Colors</span>
            <div className="flex gap-1.5">
              {playerData?.nicknameColors.map((color, index) => (
                <div
                  key={index}
                  className="w-5 h-5 rounded-full border border-glass-200/40"
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="glass-card p-4 md:p-5">
        <SectionHeader
          title="First Join Dates"
          icon={Calendar}
          iconClassName="text-blue-400"
          count={Object.keys(playerData?.firstJoinTable || {}).length}
          actions={<RedisKeyInfo redisKey={playerDataKey} />}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(playerData?.firstJoinTable || {}).map(([server, date]) => (
            <div key={server} className="subcard p-3 min-w-0">
              <div className="flex items-center gap-2 mb-1 min-w-0">
                <Globe className="w-4 h-4 text-blue-400 shrink-0" />
                <span className="text-sm text-white font-medium truncate">{server}</span>
              </div>
              <p className="text-glass-400 text-xs tabular-nums">{formatDate(date)}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )

  const renderLogs = () => {
    const flatItemHistory = itemSpawnData?.history 
      ? Object.entries(itemSpawnData.history)
          .sort(([dateA], [dateB]) => new Date(dateB).getTime() - new Date(dateA).getTime())
          .flatMap(([timestamp, items]) => items.map(item => ({ ...item, timestamp })))
      : [];

    const flatEffectHistory = effectSpawnData?.history
      ? Object.entries(effectSpawnData.history)
          .sort(([dateA], [dateB]) => new Date(dateB).getTime() - new Date(dateA).getTime())
          .flatMap(([timestamp, effects]) => effects.map(effect => ({ ...effect, timestamp })))
      : [];
      
    const reversedCreativeLog = creativeLogData ? creativeLogData.XRANGE.slice().reverse() : [];
      
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Item Spawn Log */}
          <div className="glass-card p-4 md:p-5">
            <SectionHeader
              title="Item Spawn Log"
              icon={Box}
              iconClassName="text-blue-400"
              count={flatItemHistory.length}
              actions={
                <>
                  {routeUuid && (
                    <Link
                      to={`/player-spawns?player=${encodeURIComponent(routeUuid)}`}
                      className="glass-button-sm"
                    >
                      <ArrowRight className="w-3.5 h-3.5" />
                      View in Item Spawn Log
                    </Link>
                  )}
                  <RedisKeyInfo redisKey={itemSpawnDataKey} />
                </>
              }
            />
            {flatItemHistory.length > 0 ? (
              <div className="subcard overflow-auto max-h-96">
                <table className="table">
                  <tbody>
                    {flatItemHistory.map((item, index) => (
                      <tr key={index}>
                        <td className="td !py-2 !px-3 text-xs text-glass-400 tabular-nums whitespace-nowrap">{formatDate(item.timestamp)}</td>
                        <td className="td !py-2 !px-3 font-mono text-xs text-white">{item.id}</td>
                        <td className="td !py-2 !px-3 text-right text-glass-300 tabular-nums">x{item.count}</td>
                        <td className="td !py-2 !px-3 text-right text-glass-300">{item.clusterId}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <EmptyState bare compact icon={Box} title="No item spawns" description="No item spawn data found." />}
          </div>

          {/* Effect Spawn Log */}
          <div className="glass-card p-4 md:p-5">
            <SectionHeader
              title="Effect Spawn Log"
              icon={Sparkles}
              iconClassName="text-purple-400"
              count={flatEffectHistory.length}
              actions={
                <>
                  {routeUuid && (
                    <Link
                      to={`/player-spawns?player=${encodeURIComponent(routeUuid)}`}
                      className="glass-button-sm"
                    >
                      <ArrowRight className="w-3.5 h-3.5" />
                      View in Item Spawn Log
                    </Link>
                  )}
                  <RedisKeyInfo redisKey={effectSpawnDataKey} />
                </>
              }
            />
            {flatEffectHistory.length > 0 ? (
              <div className="subcard overflow-auto max-h-96">
                <table className="table">
                  <tbody>
                    {flatEffectHistory.map((effect, index) => (
                      <tr key={index}>
                        <td className="td !py-2 !px-3 text-xs text-glass-400 tabular-nums whitespace-nowrap">{formatDate(effect.timestamp)}</td>
                        <td className="td !py-2 !px-3 font-mono text-xs text-white">{effect.id}</td>
                        <td className="td !py-2 !px-3 text-right text-glass-300 tabular-nums">{effect.seconds}s</td>
                        <td className="td !py-2 !px-3 text-right text-glass-300">{effect.clusterId}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <EmptyState bare compact icon={Sparkles} title="No effect spawns" description="No effect spawn data found." />}
          </div>
        </div>

        {/* Creative Log */}
        {creativeLogData && (
          <div className="glass-card p-4 md:p-5">
            <SectionHeader
              title="Creative Mode Log"
              icon={Swords}
              iconClassName="text-orange-400"
              count={reversedCreativeLog.length}
              actions={
                <>
                  {routeUuid && (
                    <Link
                      to={`/player-spawns?player=${encodeURIComponent(routeUuid)}`}
                      className="glass-button-sm"
                    >
                      <ArrowRight className="w-3.5 h-3.5" />
                      View in Item Spawn Log
                    </Link>
                  )}
                  <RedisKeyInfo redisKey={creativeLogDataKey} />
                </>
              }
            />
            <div className="subcard overflow-auto max-h-96">
              <table className="table">
                <thead>
                  <tr>
                    <th className="th">Timestamp</th>
                    <th className="th">Server</th>
                    <th className="th">Event</th>
                  </tr>
                </thead>
                <tbody>
                  {reversedCreativeLog.map(log => {
                    if (!log.msg) {
                      console.warn("Creative log entry is missing 'msg' property:", log);
                      return null;
                    }

                    const isGamemodeChange = log.msg.message.startsWith('GM');
                    const timestamp = new Date(parseInt(log.id.split('-')[0])).toLocaleString();
                    
                    let eventMessage = log.msg.message;
                    let EventIcon = null;

                    if (isGamemodeChange) {
                      if (log.msg.message === 'GM SURVIVAL > CREATIVE') {
                        eventMessage = "Entered Creative Mode";
                        EventIcon = LogIn;
                      } else if (log.msg.message === 'GM CREATIVE > SURVIVAL') {
                        eventMessage = "Returned to Survival Mode";
                        EventIcon = LogOut;
                      }
                    }

                    return (
                      <tr key={log.id}>
                        <td className="td !py-2 !px-3 font-mono text-xs text-glass-400 tabular-nums whitespace-nowrap">{timestamp}</td>
                        <td className="td !py-2 !px-3 text-glass-300">{log.msg.fqdn}</td>
                        <td className={cn(
                          "td !py-2 !px-3 font-mono text-xs",
                          isGamemodeChange ? "text-orange-400" : "text-white"
                        )}>
                          {EventIcon ? (
                            <div className="flex items-center gap-2">
                              <EventIcon className="w-4 h-4" />
                              <span>{eventMessage}</span>
                            </div>
                          ) : (
                            eventMessage
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }

  const renderBans = () => (
    <div className="space-y-6">
      {canViewAppeals && routeUuid && (
        <PlayerAppealsSection userUuid={routeUuid} />
      )}
      <section>
        <SectionHeader
          as="h2"
          title="Ban History"
          icon={Axe}
          iconClassName="text-red-400"
          count={bans.length}
          actions={<RedisKeyInfo redisKey={playerDataKey} />}
        />
        <div className="space-y-3">
          {bans.length > 0 ? (
            bans.map(ban => <BanCard key={ban.ban_uuid} ban={ban} canManage={canManageBans} canDelete={canDeleteBans} canViewAppeals={canViewAppeals} onDelete={handleBanDeleted} onUpdate={handleBanUpdated} onLink={() => { setItemToLink({ id: ban.ban_uuid, type: 'ban' }); setModalState({ type: 'link' }); }} />)
          ) : (
            <EmptyState icon={Axe} title="No bans" description="No bans found for this player." />
          )}
        </div>
      </section>
    </div>
  );

  const renderCompensation = () => {
    const approvedCount = compensationLogs.filter(l => l._source.status === 'APPROVED').length;
    const deniedCount = compensationLogs.filter(l => l._source.status === 'DENIED').length;

    if (compensationLoading) {
      return (
        <div className="space-y-6" aria-busy="true">
          <div className="grid grid-cols-3 gap-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="glass-card p-4 space-y-2">
                <SkeletonLoader className="h-3 w-16" />
                <SkeletonLoader className="h-7 w-12" />
              </div>
            ))}
          </div>
          {[1, 2, 3].map(i => (
            <div key={i} className="glass-card p-4 md:p-5">
              <div className="flex items-center gap-2 mb-3">
                <SkeletonLoader className="h-5 w-20 rounded-full" />
                <SkeletonLoader className="h-4 w-40" />
              </div>
              <SkeletonLoader className="h-4 w-full mb-2" />
              <SkeletonLoader className="h-4 w-5/6 mb-4" />
              <div className="flex gap-3">
                <SkeletonLoader className="h-16 w-16 rounded-lg" />
                <SkeletonLoader className="h-16 w-16 rounded-lg" />
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (compensationLogs.length === 0) {
      return (
        <EmptyState icon={HandCoins} title="No Compensation History" description="No compensation requests have been recorded for this player." />
      );
    }

    return (
      <div className="space-y-6">
        {/* Summary Stats */}
        <section>
          <SectionHeader
            as="h2"
            title="Compensation History"
            description="All compensation requests for this player."
            icon={HandCoins}
            iconClassName="text-amber-400"
          />
          <div className="grid grid-cols-3 gap-3">
            <StatTile label="Total" value={compensationTotal} icon={HandCoins} tone="neutral" />
            <StatTile label="Approved" value={approvedCount} icon={Check} tone="success" />
            <StatTile label="Denied" value={deniedCount} icon={AlertCircle} tone="danger" />
          </div>
        </section>

        {/* Request Cards */}
        <div className="space-y-4">
          {compensationLogs.map((log) => {
            const { request_id, status, timestamp, staff_id, user_reason, staff_reason, items } = log._source;
            const isApproved = status === 'APPROVED';
            const parsedDate = new Date(timestamp);

            return (
              <div
                key={log._id}
                className={cn(
                  "glass-card p-4 md:p-5 border-l-2",
                  isApproved ? "border-l-green-400" : "border-l-red-400"
                )}
              >
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                  <div className="flex items-center flex-wrap gap-2">
                    <span className={cn(
                      "badge badge-dot",
                      isApproved ? "badge-success" : "badge-danger"
                    )}>
                      {status}
                    </span>
                    <span className="text-xs font-mono text-glass-400">{request_id}</span>
                  </div>
                  <span
                    className="text-xs text-glass-400 tabular-nums"
                    title={parsedDate.toLocaleString()}
                  >
                    {formatDistanceToNow(parsedDate, { addSuffix: true })}
                  </span>
                </div>

                {/* Staff handler */}
                <div className="flex items-center gap-2 mb-4 text-sm">
                  <span className="text-glass-400">Handled by:</span>
                  <CreatorProfile creatorId={staff_id} inline />
                </div>

                {/* User Reason */}
                <div className="mb-4">
                  <p className="eyebrow mb-1.5">Player's Reason</p>
                  <div className="subcard p-3">
                    <p className="text-sm text-white whitespace-pre-wrap">{user_reason || <span className="text-glass-400 italic">No reason provided.</span>}</p>
                  </div>
                </div>

                {/* Staff Reason (only if populated) */}
                {staff_reason && (
                  <div className="mb-4">
                    <p className="eyebrow mb-1.5">Staff Reason</p>
                    <div className={cn(
                      "p-3 rounded-lg border",
                      isApproved ? "bg-green-500/5 border-green-500/15" : "bg-red-500/5 border-red-500/15"
                    )}>
                      <p className={cn(
                        "text-sm whitespace-pre-wrap",
                        isApproved ? "text-green-300" : "text-red-300"
                      )}>{staff_reason}</p>
                    </div>
                  </div>
                )}

                {/* Items */}
                {items && items.length > 0 && (
                  <div>
                    <p className="eyebrow mb-2">Items Requested ({items.length})</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
                      {items.map((item, idx) => (
                        <div key={`${log._id}-${item.item_id}-${idx}`} className="subcard p-3 relative min-w-0 overflow-hidden" title={item.item_id}>
                          <ItemIcon clusterId="" itemId={item.item_id} displayMode="card" className="w-10 h-10 mb-2 flex items-center justify-center" />
                          <span className="absolute top-1 right-1 badge badge-neutral !px-1.5">
                            ×{item.quantity}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview()
      case 'relationships':
        if (!canExploreRelationships) return renderOverview();
        return relationshipPlayerUuid ? (
          <RelationshipExplorer key={relationshipPlayerUuid} playerUuid={relationshipPlayerUuid} />
        ) : (
          <EmptyState
            icon={Network}
            title="Player identity unavailable"
            description="Refresh this profile to resolve the player before exploring their relationships."
          />
        );
      case 'logbook':
        if (!canReadLogbook || !routeUuid) return renderOverview();
        return <LogbookPlayerTimeline key={routeUuid} playerUuid={routeUuid} playerName={playerStats?.username} />;
      case 'permissions':
        return renderPermissions()
      case 'homes':
        return renderHomes()
      case 'ranks':
        return renderRanks()
      case 'economy':
        return (
          <>
            {renderEconomy()}
            {renderPlayerEconomyLog()}
          </>
        )
      case 'chat':
        return renderChat()
      case 'activity':
        return renderActivity()
      case 'logs':
        return renderLogs()
      case 'notes':
        return renderNotesAndWarnings()
      case 'bans':
        return renderBans()
      case 'compensation':
        return renderCompensation()
      default:
        return renderOverview()
    }
  }

  const playerTitle = routeUuid ? (playerStats?.username || playerData?.lastKnownName || 'Player Data') : 'Player Data';

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        eyebrow="Players"
        title={playerTitle}
        description="Player information from Redis and Users API"
        icon={User}
        backTo={routeUuid ? '/player' : undefined}
        backLabel="Player search"
        meta={routeUuid && (playerStats?.uuid || playerData) ? (
          <span className="font-mono tabular-nums">{playerStats?.uuid || routeUuid}</span>
        ) : undefined}
        actions={
          <>
            {canCreateNoteWarning && (
              <>
                <button onClick={() => setModalState({ type: 'note' })} className="glass-button"><FileText className="w-4 h-4" />Add Note</button>
                <button onClick={() => setModalState({ type: 'warning' })} className="glass-button bg-yellow-500/10 text-yellow-300 hover:bg-yellow-500/15"><TriangleAlert className="w-4 h-4" />Add Warning</button>
              </>
            )}
            {canBan && (
               <button onClick={() => setModalState({ type: 'ban' })} className="btn-danger"><Axe className="w-4 h-4" />Ban Player</button>
            )}
            {isAdminOrSuperAdmin && (
              <button
                onClick={() => setIsDebugVisible(!isDebugVisible)}
                className="glass-button-secondary"
                aria-pressed={isDebugVisible}
              >
                <Code className="w-4 h-4" />
                <span>{isDebugVisible ? 'Hide' : 'Show'} JSON</span>
              </button>
            )}
            <DataFreshness status={freshness} />
            <button
              onClick={() => routeUuid && fetchData(routeUuid, true)}
              disabled={loading}
              className="glass-button-secondary disabled:opacity-50"
              title="Refresh player data (re-read from Redis)"
            >
              <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
              <span>Refresh</span>
            </button>
          </>
        }
      />

      {/* Modals */}
      {modalState.type === 'link' && itemToLink && (
        <AddEvidenceModal
          itemId={itemToLink.id}
          itemType={itemToLink.type}
          onClose={() => { setModalState({ type: null }); setItemToLink(null); }}
          onSuccess={() => {
            setModalState({ type: null });
            setItemToLink(null);
            // Re-fetch data to show the new link.
            if (routeUuid) {
              fetchData(routeUuid);
            }
          }}
          initialTab="link"
        />
      )}
      {modalState.type === 'note' && routeUuid && (
        <CreateNoteWarningModal
          playerUuid={routeUuid}
          type="note"
          onClose={() => setModalState({ type: null })}
          onSuccess={handleModalSuccess}
        />
      )}
      {modalState.type === 'warning' && routeUuid && (
        <CreateNoteWarningModal
          playerUuid={routeUuid}
          type="warning"
          onClose={() => setModalState({ type: null })}
          onSuccess={handleModalSuccess}
        />
      )}
      {modalState.type === 'ban' && routeUuid && (
        <CreateBanModal
          playerUuid={routeUuid}
          user={user}
          onClose={() => setModalState({ type: null })}
          onSuccess={handleModalSuccess}
        />
      )}

      {/* Incident Link Modal */}
      {isIncidentLinkModalOpen && itemToLink && (
        <LinkIncidentModal
          isOpen={isIncidentLinkModalOpen}
          onClose={() => { setIsIncidentLinkModalOpen(false); setItemToLink(null); }}
          onSuccess={() => {
            setIsIncidentLinkModalOpen(false);
            setItemToLink(null);
            if (routeUuid) {
              fetchData(routeUuid);
            }
          }}
          itemId={itemToLink.id}
          itemType={itemToLink.type}
        />
      )}

      {/* Debug Box */}
      {isDebugVisible && rawJsonResponse && (
        <div className="glass-card p-4 md:p-5 animate-fade-in">
          <SectionHeader title="Raw JSON Response" icon={Code} />
          <pre className="subcard p-3 text-white overflow-x-auto text-xs font-mono">
            <code>
              {rawJsonResponse}
            </code>
          </pre>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <EmptyState
          tone="danger"
          icon={AlertCircle}
          title="Failed to load player data"
          description={error}
          action={routeUuid ? <button onClick={() => fetchData(routeUuid)} className="glass-button">Retry</button> : undefined}
        />
      )}

      {/* Loading skeleton (mirrors the tab bar + overview cards) */}
      {loading && !playerData && !playerStats && !error && (
        <div className="space-y-6" aria-busy="true">
          <div className="flex items-center gap-1 border-b border-glass-200/10 pb-2 overflow-hidden">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonLoader key={i} className="h-7 w-24 shrink-0" />
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="glass-card p-4 md:p-5">
                <div className="flex items-center gap-3 mb-4">
                  <SkeletonLoader className="w-9 h-9 rounded-lg" />
                  <div className="space-y-2">
                    <SkeletonLoader className="h-4 w-28" />
                    <SkeletonLoader className="h-3 w-20" />
                  </div>
                </div>
                <div className="space-y-3">
                  <SkeletonLoader className="h-3 w-full" />
                  <SkeletonLoader className="h-3 w-5/6" />
                  <SkeletonLoader className="h-3 w-4/6" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Player Data Display */}
      {(playerData || playerStats) && (
        <>
          {/* Tabs */}
          {isMobile ? (
               <select 
                  value={activeTab} 
                  onChange={e => handleTabClick(e.target.value)}
                  className="form-input w-full"
                  aria-label="Player data section"
              >
                  {tabs.map(tab => <option key={tab.id} value={tab.id}>{tab.label}</option>)}
              </select>
          ) : (
              <div className="tabs flex-wrap max-w-full" role="tablist" aria-label="Player profile sections">
              {tabs.map((tab) => {
                  const Icon = tab.icon
                  return (
                  <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={activeTab === tab.id}
                      onClick={() => handleTabClick(tab.id)}
                      className="tab"
                      data-active={activeTab === tab.id}
                  >
                      <Icon className="w-4 h-4" />
                      <span>{tab.label}</span>
                  </button>
                  )
              })}
              </div>
          )}

          {/* Tab Content */}
          <div className="animate-slide-up">
            {renderTabContent()}
          </div>
        </>
      )}

      {/* Empty State */}
      {!routeUuid && !loading && !error && (
        <div className="glass-card p-4 md:p-5">
          <SectionHeader title="Search for a Player" icon={Search} description="Find a player by name, UUID or IP address." />
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && isUUID(searchQuery.trim())) {
                  e.preventDefault();
                  navigate(`/player/${searchQuery.trim()}`);
                }
              }}
              className="glass-input w-full pl-9 pr-10"
              placeholder="Search by name, UUID, or IP..."
              aria-label="Search players"
            />
            {isSearching && <LoaderCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 animate-spin" />}
          </div>

          {isUUID(searchQuery.trim()) && (
            <button
              onClick={() => navigate(`/player/${searchQuery.trim()}`)}
              className="w-full mt-3 p-3 rounded-lg flex items-center justify-between gap-3 bg-accent-500/10 border border-accent-400/30 hover:bg-accent-500/15 transition-colors text-left"
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="icon-tile icon-tile-accent"><ArrowRight className="w-4 h-4" /></span>
                <div className="min-w-0">
                  <p className="text-sm text-white font-medium">Go to player profile</p>
                  <p className="text-glass-400 text-xs font-mono truncate">{searchQuery.trim()}</p>
                </div>
              </div>
              <span className="text-accent-300 text-xs flex items-center gap-1.5 shrink-0">
                <span>Press</span>
                <kbd className="kbd">Enter</kbd>
              </span>
            </button>
          )}

          {searchResults.length > 0 && (
            <div className="mt-4 divide-y divide-glass-200/10">
              {searchResults.map(result => (
                <button
                  key={result._id}
                  onClick={() => navigate(`/player/${result._source.uuid}`)}
                  className="w-full text-left px-2 py-2.5 flex items-center justify-between rounded-lg hover:bg-glass-200/5 transition-colors"
                >
                  <PlayerProfile
                    uuid={result._source.uuid}
                    name={result._source.lastKnownName}
                    nickname={result._source.nickname}
                    ipAddress={isPreciseIpSearch(debouncedSearchQuery) && result._source.lastKnownIp.startsWith(debouncedSearchQuery) ? result._source.lastKnownIp : undefined}
                    showUuid={true}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Linked Kanban Cards */}
      {routeUuid && <KanbanLinkedCardsSection linkType="player" targetId={routeUuid} title="Kanban Cards" />}

      {playerData && (
        <button 
          onClick={openChatForPlayer}
          className="glass-button"
        >
          <MessageSquare className="w-4 h-4" />
          View Player Chat Logs
        </button>
      )}
    </div>
  )
}

